import { useEffect, useState } from 'react'
import { get } from '../lib/api'
import { Pager, usePaged } from './ui'

/**
 * The rest of the workbook, read-only.
 *
 * Cash Waterfall, Loan Docs, Property DD, Lender Pipeline, Deal Intake, Card
 * Payment Plan, CPA Control and the others. They are grids, so they are shown as
 * grids — the four sheets that drive the dashboard get real components because
 * the dashboard reads named fields off them; these are for reading.
 *
 * Rows are fetched per sheet rather than with the dashboard: eleven sheets of up
 * to fifty rows is a lot to send to render one headline number.
 */

type SheetRef = { sheet: string; title: string | null; rows: number }
type SheetData = { sheet: string; title: string | null; headers: string[]; rows: string[][] }

export default function Sheets({ sheets }: { sheets: SheetRef[] }) {
  const [active, setActive] = useState<string | null>(sheets[0]?.sheet ?? null)
  const [data, setData] = useState<SheetData | null>(null)
  const [error, setError] = useState<string | null>(null)
  // CPA Control alone is 46 rows, Outreach Scripts 53. A sheet is for reading,
  // and nobody reads fifty rows of a spreadsheet in a scroll.
  const page = usePaged(data?.rows ?? [], 15)

  useEffect(() => {
    if (!active) return
    setData(null)
    setError(null)
    get<SheetData>(`/api/deal/sheet/${encodeURIComponent(active)}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load that sheet.'))
  }, [active])

  if (!sheets.length) return <p className="muted">No further sheets in this snapshot.</p>

  return (
    <div className="sheets">
      <nav className="sheets-nav">
        {sheets.map((s) => (
          <button key={s.sheet} onClick={() => setActive(s.sheet)} aria-current={active === s.sheet}>
            {s.sheet} <em>{s.rows}</em>
          </button>
        ))}
      </nav>

      {error && <p className="error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading {active}…</p>}

      {data && (
        <>
          {data.title && <p className="cc-sub sheets-title">{data.title}</p>}
          <div className="sheets-scroll">
            <table className="cc-table">
              <thead>
                <tr>{data.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {page.shown.map((row, i) => (
                  <tr key={i}>
                    {data.headers.map((_, c) => <td key={c}>{row[c] ?? ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager p={page} />
        </>
      )}
    </div>
  )
}
